// deps
import React, { ReactElement, memo, useCallback } from "react";

// internal-deps
import { useEffectOnce } from "@swiggy-private/core-ui";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { ADDRESS_LIST_TEST_IDS } from "@swiggy-private/cart-test-ids";

// constants
import { TEXTS } from "@cart-ui/constants";
import { LOCATION_NOT_FOUND, ADDRESS_LIST_SOURCE } from "@cart-ui/constants/Address";
import { Container, InfoText, InfoContainer, ActionContainer, ImageContainer, FullSuperButton } from "./styles";

export interface AddressListErrorHalfCardProps {
    reloadAddresses: VoidFunction;
    addAddress: ({ source }: { source?: string }) => void;
    onClose?: VoidFunction;
    onImpression: VoidFunction;
}

const AddressListErrorHalfCardContent = (props: AddressListErrorHalfCardProps): ReactElement => {
    const { addAddress, onImpression } = props;

    const onAddAddressClick = useCallback(() => {
        addAddress({ source: ADDRESS_LIST_SOURCE.ADDRESS_LIST_ERROR_HALF_CARD });
    }, [addAddress]);

    useEffectOnce(() => {
        onImpression();
    });

    return (
        <Container data-testid={ADDRESS_LIST_TEST_IDS.HALF_CARD_ERROR}>
            <ImageContainer $height={LOCATION_NOT_FOUND.HEIGHT}>
                <Image
                    cloudinaryId={LOCATION_NOT_FOUND.IMAGE}
                    height={`${LOCATION_NOT_FOUND.HEIGHT}px`}
                    cloudinaryHeight={LOCATION_NOT_FOUND.HEIGHT * 2}
                    cloudinaryWidth={LOCATION_NOT_FOUND.WIDTH * 2}
                    fit="cover"
                    lazyLoad={false}
                />
            </ImageContainer>
            <InfoContainer>
                <InfoText variant="V2.subheading1.neutral.bold" color="text_Highest_Emphasis">
                    {TEXTS.ADDRESS_LIST_ERROR_MSG}
                </InfoText>
            </InfoContainer>
            <ActionContainer>
                <FullSuperButton
                    onClick={onAddAddressClick}
                    role="button"
                    data-testid={ADDRESS_LIST_TEST_IDS.HALF_CARD_ERROR_CTA_ADD_ADDRESS}
                    buttonType="secondary"
                    buttonSize="large"
                    variant="small"
                >
                    {TEXTS.ADD_NEW_ADDRESS}
                </FullSuperButton>
                <FullSuperButton
                    onClick={props.reloadAddresses}
                    role="button"
                    data-testid={ADDRESS_LIST_TEST_IDS.HALF_CARD_ERROR_CTA}
                    buttonType="primary"
                    buttonSize="large"
                    variant="small"
                >
                    {TEXTS.RELOAD_ADDRESSES}
                </FullSuperButton>
            </ActionContainer>
        </Container>
    );
};

function AddressListErrorHalfCard(props: AddressListErrorHalfCardProps): ReactElement {
    const { onClose, ..._props } = props;

    const _onClose = useCallback((): void => {
        onClose && onClose();
    }, [onClose]);

    return (
        <HalfCard isOpen={true} onClose={_onClose} variant="v3" closeOnOverlayClick>
            <AddressListErrorHalfCardContent {..._props} />
        </HalfCard>
    );
}

export default memo(AddressListErrorHalfCard);
